import { addDays, isValid, startOfDay, subDays, subYears } from 'date-fns'
import { z } from 'zod'
import {
  ContractTypeValues,
  EditionModeValues,
  FamilySituationValues,
  HomeExpensesConfig,
  HomeTypeValues,
  IdTypeValues,
  SalaryMonthsValues,
  SustainableSupportValues,
  TemporaryContractTypes,
  TitleValues,
  WayTypeValues,
} from './enums'
import { getProfessionRules } from './professionConfig'
import { LoginUtil, ValidationUtils } from '@ca/common'

const requiredText = (max?: number) => {
  let schema = z.string().min(1, 'required')
  if (typeof max === 'number') {
    schema = schema.max(max, 'maxLength')
  }
  return schema
}

const optionalText = (max?: number) => {
  let schema = z.string()
  if (typeof max === 'number') {
    schema = schema.max(max, 'maxLength')
  }
  return schema.optional()
}

type DateRange = {
  min?: Date
  max?: Date
}

const requiredDate = (range?: DateRange) => {
  let schema = z.date({ error: 'required' })
  if (range?.min) {
    schema = schema.min(range.min, 'invalid')
  }
  if (range?.max) {
    schema = schema.max(range.max, 'invalid')
  }
  return schema.refine(isValid, {
    message: 'invalid',
  })
}

const optionalDate = (bounds?: DateRange) => {
  let schema = z.date()
  if (bounds?.min) {
    schema = schema.min(bounds.min, 'invalid')
  }
  if (bounds?.max) {
    schema = schema.max(bounds.max, 'invalid')
  }
  return schema.optional().refine((d) => d == null || isValid(d), {
    message: 'invalid',
  })
}

const optionalExpenseNumber = () =>
  optionalText().refine((value) => {
    if (!value?.trim()) return true
    const normalized = value.replace(',', '.')
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return false
    return Number(normalized) >= 0 && Number(normalized) <= 100_000_000
  }, 'invalid')

const TODAY_START = startOfDay(new Date())
const YESTERDAY_START = subDays(TODAY_START, 1)
const TOMORROW_START = addDays(TODAY_START, 1)

const isValidPhone = (value: string) => !!LoginUtil.isPhone(value, 'ES')?.value


/** Normalize professionCode for lookup (Zod skips superRefine when shape fails; splitting with .and() runs each part's superRefine separately). */
function getProfessionRulesFromData(data: { professionCode?: unknown }) {
  const code =
    typeof data.professionCode === 'string'
      ? data.professionCode.trim()
      : undefined
  return getProfessionRules(code)
}

const IdentitySchema = z
  .object({
    editionMode: z.enum(EditionModeValues),
    title: z.enum(TitleValues),
    firstName: requiredText(64),
    lastName1: requiredText(64),
    lastName2: requiredText(64),
    idType: z.preprocess(
      (value) => (value === '' ? undefined : value),
      z.enum(IdTypeValues, { error: 'required' })
    ),
    idNumber: requiredText(16),
    idCessationOfValidityDate: requiredDate({ min: TODAY_START }),
    nationality: requiredText(),
    birthDate: requiredDate({ max: subYears(TODAY_START, 18) }),
    birthCountry: requiredText(),
    birthTown: optionalText(64),
    phoneNumber: requiredText(32).refine(isValidPhone, 'phone.invalid'),
    phoneNumber2: optionalText(32),
    email: z.string().email('email.invalid'),
    sustainableSupport: z.enum(SustainableSupportValues),
    familySituation: z.enum(FamilySituationValues),
    familyExpenses: optionalExpenseNumber(),
    childSupportNumber: requiredText(2)
      .refine((value) => /^\d{1,2}$/.test(value), 'invalid')
      .refine((value) => Number(value) <= 99, 'range.0.99'),
  })
  .superRefine((data, ctx) => {
    if (data.idType === 'NIF' && !ValidationUtils.es.nif(data.idNumber)) {
      ctx.addIssue({
        code: 'custom',
        message: 'id.nif.invalid',
        path: ['idNumber'],
      })
    }
    if (data.idType === 'NIE' && !ValidationUtils.es.nie(data.idNumber)) {
      ctx.addIssue({
        code: 'custom',
        message: 'id.nie.invalid',
        path: ['idNumber'],
      })
    }
  })

const ResidenceSchema = z
  .object({
    wayType: z.enum(WayTypeValues),
    wayDescription: requiredText(64),
    wayNumber: requiredText(5).refine(
      (value) => /^\d+$/.test(value),
      'invalid'
    ),
    floor: optionalText(4),
    letter: optionalText(4),
    additionalAddress: optionalText(64),
    zipCode: requiredText(5).refine(
      (value) => /^\d{5}$/.test(value),
      'invalid'
    ),
    town: requiredText(64),
    country: requiredText(),
    homeType: z.enum(HomeTypeValues),
    homeExpenses: optionalExpenseNumber(),
    entranceDate: requiredText(4)
      .refine((value) => /^\d{4}$/.test(value), 'invalid')
      .refine((value) => parseInt(value, 10) > 1900, 'invalid'),
  })
  .superRefine((data, ctx) => {
    if (
      HomeExpensesConfig[data.homeType]?.required &&
      !data.homeExpenses?.trim()
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'required',
        path: ['homeExpenses'],
      })
    }
  })

const EmploymentSchema = z
  .object({
    professionCode: requiredText(),
    contractType: z.enum(ContractTypeValues).optional(),
    contractStartDate: optionalDate({ max: YESTERDAY_START }),
    contractEndDate: optionalDate({ min: TOMORROW_START }),
    salary: optionalText().refine((value) => {
      if (!value?.trim()) return true
      const normalized = value.replace(',', '.')
      if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return false
      return Number(normalized) <= 100_000_000
    }, 'invalid'),
    salaryMonthsNumber: z.enum(SalaryMonthsValues).optional(),
    companyName: optionalText(64),
    // companyId: optionalText(9).refine(
    //   (value) => !value || /^[A-Z]\d{7}[A-Z0-9]$/i.test(value),
    //   'invalid'
    // ),
    companyWayType: z.enum(WayTypeValues).optional(),
    companyWayDescription: optionalText(255),
    companyWayNumber: optionalText(5).refine(
      (value) => !value || /^\d+$/.test(value),
      'invalid'
    ),
    companyZipCode: optionalText(5).refine(
      (value) => !value || /^\d{5}$/.test(value),
      'invalid'
    ),
    companyTown: optionalText(64),
    companyPhone: optionalText(32),
  })
  .superRefine((data, ctx) => {
    if (
      data.contractType &&
      TemporaryContractTypes.includes(data.contractType) &&
      !data.contractEndDate
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'required',
        path: ['contractEndDate'],
      })
    }
    if (
      data.contractStartDate &&
      data.contractEndDate &&
      data.contractEndDate <= data.contractStartDate
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'invalid',
        path: ['contractEndDate'],
      })
    }
    const professionRules = getProfessionRulesFromData(data)
    if (professionRules) {
      if (professionRules.salary === 'required' && !data.salary?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'required',
          path: ['salary'],
        })
      }
      if (professionRules.contractType === 'required' && !data.contractType) {
        ctx.addIssue({
          code: 'custom',
          message: 'required',
          path: ['contractType'],
        })
      }
      if (
        professionRules.contractStartDate === 'required' &&
        !data.contractStartDate
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'required',
          path: ['contractStartDate'],
        })
      }
      if (professionRules.salary === 'required' && !data.salaryMonthsNumber) {
        ctx.addIssue({
          code: 'custom',
          message: 'required',
          path: ['salaryMonthsNumber'],
        })
      }
    }
  })

const BankingSchema = z.object({
  iban: requiredText(34).refine((v) => ValidationUtils.iban.validate(v, ['ES']), 'iban.invalid'),
  bankAccountHolderName: requiredText(255),
  bankAccountOpeningDate: optionalDate({ max: TODAY_START }),
  editionMode: z.enum(EditionModeValues),
})

const TermsSchema = z
  .object({
    vedaconAcceptance: z.boolean(),
    internalOptin: z.boolean(),
    partnerOptin: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.vedaconAcceptance !== true) {
      ctx.addIssue({
        code: 'custom',
        message: 'required',
        path: ['vedaconAcceptance'],
      })
    }
    if (data.internalOptin !== true) {
      ctx.addIssue({
        code: 'custom',
        message: 'required',
        path: ['internalOptin'],
      })
    }
    if (data.partnerOptin !== true) {
      ctx.addIssue({
        code: 'custom',
        message: 'required',
        path: ['partnerOptin'],
      })
    }
  })

/** Merged with .and() so each part's superRefine runs independently; Zod collects errors from all branches. */
export const CofidisMonthlyApplicationSchema = IdentitySchema.and(
  ResidenceSchema
)
  .and(EmploymentSchema)
  .and(BankingSchema)
  .and(TermsSchema)
